import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, Brain, Target, Star, Book, Code } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Button from '../ui/Button';

interface SkillAssessmentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const questions = [
  {
    id: 'learning_style',
    question: 'How do you prefer to learn new skills?',
    options: [
      { id: 'practical', text: 'Hands-on practice and projects' },
      { id: 'theoretical', text: 'Reading and theoretical understanding' },
      { id: 'mixed', text: 'A mix of theory and practice' },
      { id: 'social', text: 'Learning from peers and mentors' }
    ]
  },
  {
    id: 'time_commitment',
    question: 'How much time can you dedicate to skill development weekly?',
    options: [
      { id: '0-5', text: '0-5 hours' },
      { id: '5-10', text: '5-10 hours' },
      { id: '10-20', text: '10-20 hours' },
      { id: '20+', text: '20+ hours' }
    ]
  },
  {
    id: 'skill_level',
    question: 'How would you rate your current technical skills?',
    options: [
      { id: 'beginner', text: 'Beginner - Just starting out' },
      { id: 'intermediate', text: 'Intermediate - Some experience' },
      { id: 'advanced', text: 'Advanced - Significant experience' },
      { id: 'expert', text: 'Expert - Professional level' }
    ]
  },
  {
    id: 'goal_timeline',
    question: 'What is your target timeline for achieving your next career goal?',
    options: [
      { id: '3-months', text: '3 months' },
      { id: '6-months', text: '6 months' },
      { id: '1-year', text: '1 year' },
      { id: '2-years', text: '2+ years' }
    ]
  },
  {
    id: 'learning_priority',
    question: 'What is your primary focus for skill development?',
    options: [
      { id: 'technical', text: 'Technical skills (programming, tools)' },
      { id: 'soft', text: 'Soft skills (communication, leadership)' },
      { id: 'domain', text: 'Domain knowledge (industry expertise)' },
      { id: 'certification', text: 'Professional certifications' }
    ]
  }
];

const SkillAssessmentModal: React.FC<SkillAssessmentModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState<Record<string, string>>({});

  const handleAnswer = (questionId: string, answerId: string) => {
    setResponses(prev => ({ ...prev, [questionId]: answerId }));
    
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Save responses to localStorage
      localStorage.setItem('skillAssessment', JSON.stringify({
        responses,
        date: new Date().toISOString()
      }));
      
      // Navigate to skills page
      navigate('/skills');
    }
  };

  const getIcon = (index: number) => {
    const icons = [Brain, Target, Star, Book, Code];
    const Icon = icons[index];
    return <Icon className="h-6 w-6 text-primary-600" />;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={onClose}
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 md:p-8"
            >
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-6 w-6" />
              </button>

              {/* Progress bar */}
              <div className="mb-8">
                <div className="flex justify-between text-sm text-gray-500 mb-2">
                  <span>Question {currentStep + 1} of {questions.length}</span>
                  <span>{Math.round(((currentStep + 1) / questions.length) * 100)}% Complete</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full">
                  <div
                    className="h-2 bg-primary-600 rounded-full transition-all duration-300"
                    style={{ width: `${((currentStep + 1) / questions.length) * 100}%` }}
                  />
                </div>
              </div>

              {/* Question */}
              <div className="mb-8">
                <div className="flex items-center mb-6">
                  {getIcon(currentStep)}
                  <h2 className="text-2xl font-bold text-gray-900 ml-3">
                    {questions[currentStep].question}
                  </h2>
                </div>

                <div className="grid gap-4">
                  {questions[currentStep].options.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => handleAnswer(questions[currentStep].id, option.id)}
                      className="w-full text-left p-6 rounded-xl border-2 hover:border-primary-500 hover:bg-primary-50 transition-all duration-200 group"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-medium text-gray-900 group-hover:text-primary-600">
                          {option.text}
                        </span>
                        <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-primary-600 transform group-hover:translate-x-1 transition-all" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default SkillAssessmentModal;